import type { ShiftOutcome } from '../../partner/index.js'
import type { WorkerNotification } from '../../messages/index.js'
import type { WorkerTier } from '../../core/index.js'
import type { WorkerDetailHooks } from '../../roster/index.js'
import { TIER_LABELS } from '../../core/index.js'
import { PeriodView } from './PeriodView.js'
import type { DayRules } from './MonthCalendar.js'
import { formatMonth } from './outcomeGroups.js'
import { breakdownFromScore, notificationsInMonth, shiftsInMonth } from './periodData.js'
import { LoadFailure } from '../../core/index.js'

interface HistoryPeriodProps {
  workerId: string
  /** The read for one month's score, from whichever app the panel is in. */
  useScoreForPeriod: WorkerDetailHooks['useScoreForPeriod']
  /** `YYYY-MM`. */
  month: string
  /** Every outcome and message the worker has; narrowed to the month here. */
  outcomes: readonly ShiftOutcome[]
  notifications: readonly WorkerNotification[]
  /** The tier the worker holds now, for a month whose score placed them in none. */
  heldTier: WorkerTier
  rules: DayRules
  /** How the outcomes read is getting on, so an opened month's Shifts tab says so rather than "no shifts". */
  shiftsStatus?: 'pending' | 'error'
}

function dialNote(score: { kind: 'running' | 'final'; tier: WorkerTier | null; provisional: boolean } | null): string {
  if (!score) {
    return 'No score for this month'
  }
  const parts = [score.kind === 'final' ? 'Final' : 'Running', ...(score.tier ? [TIER_LABELS[score.tier]] : []), ...(score.provisional ? ['provisional'] : [])]
  return parts.join(' · ')
}

/**
 * One past month opened from the history: the same view as the current
 * period, fed by that month's score read (`?period=`), with the breakdown
 * taken from the engine result stored with it. Rendered only while the
 * month is open, so the read happens on demand.
 */
export function HistoryPeriod({ workerId, useScoreForPeriod, month, outcomes, notifications, heldTier, rules, shiftsStatus }: HistoryPeriodProps) {
  const scoreQuery = useScoreForPeriod(workerId, month)

  if (scoreQuery.isPending) {
    return <div className="text-[11px] text-ink-mid">Loading {formatMonth(month)}…</div>
  }
  if (scoreQuery.isError) {
    return <LoadFailure size="xs" message={`Could not load this worker’s score for ${formatMonth(month)}.`} onRetry={() => void scoreQuery.refetch()} retrying={scoreQuery.isFetching} />
  }

  const score = scoreQuery.data
  return (
    <PeriodView
      period={{ month, score, breakdown: breakdownFromScore(score), shifts: shiftsInMonth(outcomes, month), notifications: notificationsInMonth(notifications, month) }}
      dialLabel={formatMonth(month)}
      dial={{ score: score?.score ?? null, tier: score?.tier ?? heldTier, onTrack: null }}
      dialNote={dialNote(score)}
      rules={rules}
      shiftsStatus={shiftsStatus}
    />
  )
}
